import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Task, TaskCreatePayload } from '../lib/services/tasksService';

interface TaskFormProps {
  task?: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: TaskCreatePayload & { completed?: boolean }) => Promise<{ success: boolean; error?: string }>;
  title?: string;
}

const TaskForm: React.FC<TaskFormProps> = ({
  task,
  isOpen,
  onClose,
  onSubmit,
  title = 'Nouvelle tâche',
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description ?? '',
        due_date: task.due_date,
      });
    } else {
      setFormData({
        title: '',
        description: '',
        due_date: new Date().toISOString().split('T')[0],
      });
    }
    setError(null);
  }, [task, isOpen]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const titleTrim = formData.title.trim();
    if (!titleTrim) {
      setError('Le titre est obligatoire');
      return;
    }
    if (!formData.due_date) {
      setError('La date d’échéance est obligatoire');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: titleTrim,
        description: formData.description.trim() || null,
        due_date: formData.due_date,
      };
      const result = await onSubmit(payload);
      if (result.success) onClose();
      else setError(result.error ?? 'Erreur lors de l’enregistrement');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-700 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-dark-700 sticky top-0 bg-dark-800 z-10">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="task-title" className="block text-sm font-medium text-dark-300 mb-2">
              Titre *
            </label>
            <input
              id="task-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent"
              placeholder="Ex: Rendez-vous banque"
              disabled={loading}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="task-description" className="block text-sm font-medium text-dark-300 mb-2">
              Description (optionnel)
            </label>
            <textarea
              id="task-description"
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent resize-y min-h-[160px] text-base leading-relaxed"
              placeholder="Détails, notes, article…"
              rows={8}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="task-due_date" className="block text-sm font-medium text-dark-300 mb-2">
              Date d’échéance *
            </label>
            <input
              id="task-due_date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData((p) => ({ ...p, due_date: e.target.value }))}
              className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent"
              disabled={loading}
              required
            />
          </div>
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg bg-dark-700 text-white font-medium hover:bg-dark-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;
